import React from "react";
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate, useLocation } from "react-router-dom";
import "./App.css";
import CodingPage from "../pages/Coding";
import HomePage from "../pages/Home";
import Toggle from "../../components/Toggle";
import { RemoveScroll } from "react-remove-scroll";
import SignupPage from "../pages/Signup";
import LoginPage from "../pages/Login";
import ForgotPasswordPage from "../pages/ForgotPassword";
import InterviewsPage from "../pages/Interviews";
import Interview from "../pages/Interview";
import DashboardPage from "../pages/Dashboard";
import CompanyInterviewEditor from "../pages/CompanyInterviewEditor";
import CandidateResults from "../pages/CandidateResults";
import CliDocs from "../pages/CliDocs";
import CreditsPage from "../pages/CreditsPage";
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { AppState, CodingPageWrapperProps, InterviewPageWrapperProps, RouteParams } from "../../types";

/** Disable RemoveScroll on these routes so the page can scroll (home, dashboard, etc.) */
function useIsScrollableRoute(): boolean {
    const location = useLocation();
    const path = location.pathname;
    return path === '/' || path === '/dashboard' || path === '/dashboard/settings' || path === '/dashboard/credits' || path.startsWith('/interviews') || path.startsWith('/company/') || path.startsWith('/docs/');
}

function AppScrollWrapper({ children }: { children: React.ReactNode }): React.ReactElement {
    const isScrollableRoute = useIsScrollableRoute();
    return (
        <RemoveScroll enabled={!isScrollableRoute}>
            {children}
        </RemoveScroll>
    );
}

// Wrapper component to provide params to class component
function CodingPageWrapper(props: CodingPageWrapperProps): React.ReactElement {
    const params = useParams<RouteParams>();
    return <CodingPage {...props} sessionid={params.sessionid || ""} />;
}

// Wrapper component to provide router props to Interview class component
function InterviewPageWrapper(props: InterviewPageWrapperProps): React.ReactElement {
    const params = useParams<RouteParams>();
    return <Interview {...props} match={{ params: { sessionid: params.sessionid || "" } }} />;
}

// Wrapper component for InterviewsPage to provide history prop
function InterviewsPageWrapper(props: { darkMode: boolean }): React.ReactElement {
    const navigate = useNavigate();
    return <InterviewsPage {...props} history={{ push: navigate }} />;
}

export default class App extends React.Component<{}, AppState> {
    private boundKeyHandler?: (event: KeyboardEvent) => void;

    constructor(props: {}) {
        super(props);
        const storedDarkMode = sessionStorage.getItem("darkMode");
        const initialDarkMode = storedDarkMode === "true";
        this.state = {
            message: null,
            darkMode: initialDarkMode,
            user: null,
            login: false,
            uid: undefined
        };
        this.setMode = this.setMode.bind(this);
        this.simulateMouseClick = this.simulateMouseClick.bind(this);
        this.handleGlobalKeyDown = this.handleGlobalKeyDown.bind(this);
    }

    onKeyUp(keyName: string, e: KeyboardEvent, handle: any): void {
        this.setState({
            output: `onKeyUp ${keyName}`,
        });
    }

    onKeyDown(keyName: string, e: KeyboardEvent, handle: any): void {
        this.setState({
            output: `onKeyDown ${keyName}`,
        });
        const element = document.querySelector('.toggle__handler');
        if (element) {
            this.simulateMouseClick(element);
        }
    }

    handleGlobalKeyDown = (event: KeyboardEvent): void => {
        // Check if the user is currently typing in an input field or editor
        const activeElement = document.activeElement;
        const isTyping = activeElement && (
            activeElement.tagName === 'INPUT' ||
            activeElement.tagName === 'TEXTAREA' ||
            (activeElement as HTMLElement).contentEditable === 'true' ||
            activeElement.classList.contains('CodeMirror') ||
            activeElement.closest('.CodeMirror') ||
            activeElement.closest('input') ||
            activeElement.closest('textarea')
        );

        // If user is typing, completely ignore the event (don't even log it)
        if (isTyping) {
            return; // Exit early, don't interfere with typing at all
        }

        // Check for Shift + T
        if (event.shiftKey && event.key.toLowerCase() === 't') {
            event.preventDefault();
            event.stopPropagation();
            this.setMode();

            // Simulate a click on evalOut section to restore focus state
            setTimeout(() => {
                const evalOutElement = document.querySelector('.evalOut');
                if (evalOutElement) {
                    (evalOutElement as HTMLElement).click();
                } else {
                    const codingArea = document.querySelector('.CodeMirror') || document.querySelector('.upperHalf');
                    if (codingArea) {
                        (codingArea as HTMLElement).click();
                    }
                }
            }, 100); // Small delay to ensure setMode completes
        }
        // Check for Alt + S (keeping the alternative shortcut)
        if (event.altKey && event.key.toLowerCase() === 's') {
            event.preventDefault();
            event.stopPropagation();
            this.setMode();

            // Simulate a click on evalOut section to restore focus state
            setTimeout(() => {
                const evalOutElement = document.querySelector('.evalOut');
                if (evalOutElement) {
                    (evalOutElement as HTMLElement).click();
                } else {
                    const codingArea = document.querySelector('.CodeMirror') || document.querySelector('.upperHalf');
                    if (codingArea) {
                        (codingArea as HTMLElement).click();
                    }
                }
            }, 100); // Small delay to ensure setMode completes
        }
    };

    simulateMouseClick(element: Element | null): void {
        if (!element) return;

        const mouseClickEvents = ['mousedown', 'click', 'mouseup'];
        mouseClickEvents.forEach(mouseEventType => {
            element.dispatchEvent(
                new MouseEvent(mouseEventType, {
                    view: window,
                    bubbles: true,
                    cancelable: true,
                    buttons: 1
                })
            );
        });
    }

    setMode = (): void => {
        this.setState({ darkMode: !this.state.darkMode });
        sessionStorage.setItem("darkMode", String(!this.state.darkMode));
    };

    componentDidMount(): void {
        // Add global keyboard event listener
        const boundHandler = (event: KeyboardEvent) => this.handleGlobalKeyDown(event);

        document.addEventListener('keydown', boundHandler, true); // Use capture phase
        window.addEventListener('keydown', boundHandler, true);

        this.boundKeyHandler = boundHandler;

        firebase.auth().onAuthStateChanged((user) => {
            if (user) {
                const uid = user.uid;
                this.setState({
                    user: user,
                    login: true,
                    uid: uid
                });
            } else {
                this.setState({
                    login: false,
                });
            }
        });

        // Initialize Crisp chat with configuration to hide launcher
        (window as any).$crisp = [];
        (window as any).CRISP_WEBSITE_ID = "8cf64773-bd33-4bfa-9b90-008d5d033135";

        // Configure Crisp to hide launcher but keep chat functional
        (window as any).$crisp.push(['set', 'chat:visible', false]);
        (window as any).$crisp.push(['set', 'chat:opened', false]);

        // Add CSS to hide launcher initially
        const hideLauncherStyle = document.createElement('style');
        hideLauncherStyle.textContent = `
            .crisp-client .crisp-client-launcher { 
                display: none !important; 
            }
        `;
        document.head.appendChild(hideLauncherStyle);

        (function () {
            const d = document;
            const s = d.createElement("script");
            s.src = "https://client.crisp.chat/l.js";
            s.async = true;
            d.getElementsByTagName("head")[0].appendChild(s);
        })();

        // Hide Crisp chat launcher button immediately and keep it hidden
        setTimeout(() => {
            if ((window as any).$crisp) {
                (window as any).$crisp.push(['do', 'chat:hide']);
            }
        }, 100); // Reduced timeout to hide immediately


        const mq = window.matchMedia('(prefers-color-scheme: dark)');

        mq.addEventListener('change', (evt) => {
            const darkMode = evt.matches;
            this.setState({ darkMode });
            sessionStorage.setItem("darkMode", String(darkMode));
        });

        // Set initial body/html class based on darkMode state (for consistent background)
        const root = document.documentElement;
        if (this.state.darkMode) {
            document.body.classList.add('dark-mode');
            root.classList.add('dark-mode');
        } else {
            document.body.classList.remove('dark-mode');
            root.classList.remove('dark-mode');
        }
    }

    componentDidUpdate(prevProps: {}, prevState: AppState): void {
        // Update body/html class when darkMode changes (for consistent background)
        if (prevState.darkMode !== this.state.darkMode) {
            const root = document.documentElement;
            if (this.state.darkMode) {
                document.body.classList.add('dark-mode');
                root.classList.add('dark-mode');
            } else {
                document.body.classList.remove('dark-mode');
                root.classList.remove('dark-mode');
            }
        }
    }

    componentWillUnmount(): void {
        // Clean up the global event listener
        if (this.boundKeyHandler) {
            document.removeEventListener('keydown', this.boundKeyHandler);
        }
    }

    render(): React.ReactElement {
        return (
            <React.StrictMode>
                <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                    <AppScrollWrapper>
                        <div className={this.state.darkMode === false ? "App" : "App-dark"}>
                            <Toggle
                                setMode={this.setMode}
                                darkMode={this.state.darkMode}
                                simulateMouseClick={this.simulateMouseClick.bind(this)}
                            />
                            <Routes>
                                <Route path="/" element={<HomePage darkMode={this.state.darkMode} navigate={() => { }} />} />
                                <Route path="/box/:sessionid" element={<CodingPageWrapper darkMode={this.state.darkMode} />} />
                                <Route path="/letsgo" element={<SignupPage darkMode={this.state.darkMode} />} />
                                <Route path="/signup" element={<SignupPage darkMode={this.state.darkMode} />} />
                                <Route path="/login" element={<LoginPage darkMode={this.state.darkMode} />} />
                                <Route path="/forgot-password" element={<ForgotPasswordPage darkMode={this.state.darkMode} />} />
                                <Route path="/interviews" element={<DashboardPage darkMode={this.state.darkMode} />} />
                                <Route path="/interviews/:sessionid" element={<InterviewPageWrapper darkMode={this.state.darkMode} uid={this.state.uid} />} />
                                <Route path="/dashboard" element={<DashboardPage darkMode={this.state.darkMode} />} />
                                <Route path="/dashboard/settings" element={<DashboardPage darkMode={this.state.darkMode} />} />
                                <Route path="/dashboard/credits" element={<CreditsPage darkMode={this.state.darkMode} />} />
                                <Route path="/company/interview/:interviewId" element={<CompanyInterviewEditor darkMode={this.state.darkMode} />} />
                                <Route path="/company/results/:interviewId" element={<CandidateResults darkMode={this.state.darkMode} />} />
                                <Route path="/docs/cli" element={<CliDocs darkMode={this.state.darkMode} />} />
                            </Routes>
                        </div>
                    </AppScrollWrapper>
                </Router>
            </React.StrictMode>
        );
    }
}
